
import { defineComponent } from 'vue';
import { mapState } from 'pinia';
import { useStore } from '@/plugins/store/index';
import GenericTable from '@/components/tables/GenericTable.vue';
import { roundIfNecessary } from '@/util/helpers/numbers';
import { AllFilterSelectors, Session, Theme } from '@/types';
import CourseThemesModal from '@/views/Courses/CourseThemesModal.vue';

export default defineComponent({
    components: { GenericTable, CourseThemesModal },
    data() {
        function cellRendererSelector(params: any) {
            if (params.node.rowPinned) {
                return {
                    component: 'CustomPinnedRowCellRenderer',
                };
            }
            return {
                component: 'GenericCellRenderer',
            };
        }
        return {
            selectedModalCourse: null,
            headers: [
                {
                    headerName: 'Forløbsnavn',
                    field: 'title',
                    sortable: true,
                    cellRendererSelector,
                },
                {
                    headerName: 'Antal startet',
                    field: 'started',
                    sortable: true,
                    cellRendererSelector,
                },
                {
                    headerName: 'Antal afsluttet',
                    field: 'ended',
                    sortable: true,
                    cellRendererSelector,
                },
                {
                    headerName: 'Procentuelt afsluttet',
                    field: 'percentageEnded',
                    sortable: true,
                    cellRendererSelector,
                },
            ],
        };
    },
    computed: {
        ...mapState(useStore, ['courses', 'themes', 'sessions', 'selectedCourse', 'courseStatistics']),
        pinnedBottomRowData(): any {
            const courseDistribution = this.items.reduce(
                (accum, current) => {
                    accum.started += current.started;
                    accum.ended += current.ended;
                    return accum;
                },
                { started: 0, ended: 0 }
            );
            return [
                {
                    id: AllFilterSelectors.AllCourses,
                    title: 'Total',
                    started: courseDistribution.started,
                    ended: courseDistribution.ended,
                    percentageEnded: roundIfNecessary((courseDistribution.ended / courseDistribution.started) * 100, 1) || 0,
                },
            ];
        },
        items(): any[] {
            if (!this.courses || !this.themes || !this.sessions || !this.courseStatistics) {
                return [];
            }

            const themesMappedById = this.themes.reduce((accum, current) => {
                accum[`themes/${current.id}`] = current;
                return accum;
            }, {} as { [key: string]: Theme });
            const sessionsMappedById = this.sessions.reduce((accum, current) => {
                accum[`sessions/${current.id}`] = current;
                return accum;
            }, {} as { [key: string]: Session });

            return [
                ...this.courses
                    .filter((currentCourse) => currentCourse.id !== AllFilterSelectors.AllCourses)
                    .map((currentCourse) => {
                        const startedForCurrentCourse = this.courseStatistics.filter((currentCourseStatistic) => currentCourseStatistic.ref === currentCourse.id);
                        const endedForCurrentCourse = startedForCurrentCourse.filter((currentCourseStatistic) => currentCourseStatistic.ended);

                        const themesWithSessions = startedForCurrentCourse.reduce((accum, current) => {
                            if (!current.themesWithSessions) {
                                return accum;
                            }

                            Object.keys(current.themesWithSessions).forEach((currentThemeKey) => {
                                const currentTheme = themesMappedById[currentThemeKey];
                                if (!currentTheme) {
                                    return;
                                }
                                Object.keys(current.themesWithSessions![currentThemeKey]).forEach((currentSessionKey) => {
                                    const currentSession = sessionsMappedById[currentSessionKey];
                                    if (!currentSession) {
                                        return;
                                    }
                                    if (!accum[`${currentTheme.id}/${currentSession.id}`]) {
                                        accum[`${currentTheme.id}/${currentSession.id}`] = {
                                            theme: currentTheme,
                                            session: currentSession,
                                            statistics: {
                                                completed: 0,
                                                skipped: 0,
                                                unhandled: 0,
                                            },
                                        };
                                    }
                                    accum[`${currentTheme.id}/${currentSession.id}`].statistics[current.themesWithSessions![currentThemeKey][currentSessionKey]]++;
                                });
                            });

                            return accum;
                        }, {} as any);

                        return {
                            id: currentCourse.id,
                            title: currentCourse.title,
                            started: startedForCurrentCourse.length,
                            ended: endedForCurrentCourse.length,
                            themesWithSessions,
                            percentageEnded: roundIfNecessary((endedForCurrentCourse.length / startedForCurrentCourse.length) * 100, 1) || 0,
                        };
                    }),
            ];
        },
    },
    methods: {
        onCourseClicked(value: any) {
            this.selectedModalCourse = value;
            (this.$refs['modal-input'] as HTMLInputElement)?.click();
        },
    },
});
